import axiosClient from "../../utils/axiosInstance";
import {
  AddUserToCoursePayloadType,
  CourseUsersType,
  RemoveUserFromCoursePayloadType,
} from "../types/adminCourseTypes";
import { ArticleType } from "../types/LearnTypes";

export const retrieveAdminArticles = (
  pageNumber: number,
  resultsPerPage: number,
  articleType: ArticleType = ArticleType.course,
  search?: string,
  filter?: string,
  sortBy?: string
) => {
  try {
    const response = axiosClient().get(
      `/learn/articles/${articleType}/${pageNumber}`,
      {
        params: { resultsPerPage, search, filter, sortBy },
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const retrieveAdminCourseUsers = (
  courseId: string,
  pageNumber: number,
  resultsPerPage: number,
  userRole: CourseUsersType,
  searchString?: string
) => {
  try {
    const response = axiosClient().get(
      `/learn/course/users/${courseId}/${pageNumber}`,
      {
        params: {
          resultsPerPage,
          userRole,
          search: searchString,
        },
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const retrieveAdminCourseUserInteractionById = (
  courseId: string,
  userInteractionId: string,
  userRole: CourseUsersType
) => {
  try {
    const response = axiosClient().get(
      `/learn/course/user/${courseId}/${userInteractionId}?userRole=${userRole}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const retrieveAdminCourseById = (courseId: string) => {
  try {
    const response = axiosClient().get(`/learn/course-by-id/${courseId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const retrieveStudentsSolutionInCourseForFreeResponse = (
  userId: string,
  courseId: string,
  pageNumber: number,
  resultsPerPage: number
) => {
  try {
    const response = axiosClient().get(
      `/reviewer/retrieve/free-response/user/submitted/solutions/${userId}/${courseId}/${pageNumber}?resultsPerPage=${
        resultsPerPage || 10
      }`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const retrieveStudentsSolutionInCourseForMcq = (
  userId: string,
  courseId: string,
  pageNumber: number,
  resultsPerPage: number
) => {
  try {
    const response = axiosClient().get(
      `/reviewer/retrieve/mcq/user/submitted/solutions/${userId}/${courseId}/${pageNumber}?resultsPerPage=${
        resultsPerPage || 10
      }`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const updateAdminReviewForUsersFrqSolution = (payload: any) => {
  try {
    const response = axiosClient().post(
      "/reviewer/submit/free-response/review",
      {
        ...payload,
      }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Sends an email with an optional attachment.
 * @param to The recipient's email address.
 * @param message The email message body.
 * @param subject The email subject.
 * @param base64Data Optional base64 encoded data for the attachment.
 * @return A promise that resolves to the response of the email sending request.
 * @throws An error if the email sending request fails.
 */
export const sendAttachedEmail = async (
  to: string,
  message: string,
  subject: string,
  base64Data?: string
) => {
  try {
    const response = await axiosClient().post("/user/send-attachment-email", {
      to,
      message,
      subject,
      base64Data,
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getBase64ImageFromURL = async (url: string) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    // Get base64 data
    const data = await new Promise((resolve) => {
      reader.onloadend = () => resolve(reader.result);
    });
    return data;
  } catch (error) {
    console.error("Error fetching and converting image:", error);
  }
};

export const updateUserCertificateStatus = async (
  userId: string,
  courseId: string,
  status: boolean
) => {
  try {
    const response = await axiosClient().put(
      `/learn/user-certificate-status/${userId}/${courseId}`,
      { certificateStatus: status }
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteArticleAsync = async (
  courseId: string,
  articleType: ArticleType
) => {
  try {
    const response = await axiosClient().delete(
      `/learn/article/${articleType}/${courseId}`
    );
    return response;
  } catch (error) {
    console.error("[!] delete course errored ", error);
    throw error;
  }
};

export const addNewUserToCourse = (payload: AddUserToCoursePayloadType) => {
  // courseId,newUserEmail,classId,orgId,userRole : payload has these much fields
  const response = axiosClient().put("/learn/course/register/user", {
    ...payload,
  });
  return response;
};

export const removeUserFromCourse = (
  payload: RemoveUserFromCoursePayloadType
) => {
  // courseId,removingUserId : payload has these much fields
  const response = axiosClient().delete(
    `/learn/course/remove/user/${payload.removingUserId}/${payload.courseId}`,
  );
  return response;
};
